import React, { useCallback, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { ARIA_SUPPORTED_LANGUAGES } from '../../../../pure-js/libs/assistantLanguage'
import { AssistantUiKey } from '../../../../pure-js/libs/assistantMessages'
import { INTERAX_COLORS } from '../../constants/colors'
import { TESTIDS } from '../../config/testids'
import { useTranslation } from '../hooks/useIntractionTranslate'

export interface AriaLanguagePickerProps {
  /** Called after the language changed, e.g. to restart a live session on the new voice. */
  onLanguageChanged?: () => void
}

/**
 * Switches the language Aria speaks, which is the app language. Aria's language is derived from the UI
 * locale on every request, so as one setting the two can never disagree.
 */
export const AriaLanguagePicker: React.FC<AriaLanguagePickerProps> = ({ onLanguageChanged }) => {
  const { t, i18n } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const current =
    ARIA_SUPPORTED_LANGUAGES.find((language) => i18n.language?.startsWith(language.locale)) ??
    ARIA_SUPPORTED_LANGUAGES[0]

  const close = useCallback(() => setAnchorEl(null), [])

  const select = useCallback(
    async (locale: string) => {
      setAnchorEl(null)
      if (i18n.language === locale) return
      await i18n.changeLanguage(locale)
      onLanguageChanged?.()
    },
    [i18n, onLanguageChanged]
  )

  return (
    <>
      <Tooltip title={t(AssistantUiKey.LANGUAGE_PICKER_LABEL)}>
        <IconButton
          aria-label={t(AssistantUiKey.LANGUAGE_PICKER_LABEL)}
          data-testid={TESTIDS.ARIA_LANGUAGE_BTN}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size="small"
          sx={{ color: INTERAX_COLORS.white70, px: 1, borderRadius: 999 }}
        >
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em' }}>
            {current.shortLabel}
          </Typography>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        slotProps={{ paper: { sx: { backgroundColor: INTERAX_COLORS.offBlack, color: INTERAX_COLORS.white } } }}
      >
        {ARIA_SUPPORTED_LANGUAGES.map((language) => (
          <MenuItem
            key={language.locale}
            selected={language.locale === current.locale}
            onClick={() => void select(language.locale)}
            data-testid={`${TESTIDS.ARIA_LANGUAGE_OPTION}-${language.locale}`}
            sx={{
              fontSize: 14,
              '&.Mui-selected': { backgroundColor: INTERAX_COLORS.sun20, color: INTERAX_COLORS.sun }
            }}
          >
            {language.nativeName}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default AriaLanguagePicker
